import { useEffect, useRef, useState } from 'react';
import { handleTelegramAuth } from '../../services/telegram-auth';

interface TelegramAuthProps {
    onAuth: (user: any) => void;
}

declare global {
    interface Window {
        TelegramLoginWidget: {
            dataOnauth: (user: any) => void;
        };
    }
}

const TelegramAuth = ({ onAuth }: TelegramAuthProps) => {
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const telegramRef = useRef<HTMLDivElement>(null);
    const botName = import.meta.env.VITE_TELEGRAM_BOT_NAME;

    useEffect(() => {
        // Проверка и логирование настроек бота
        console.log('Имя бота из переменных окружения:', botName);

        if (!botName) {
            setError('Имя бота Telegram не указано в переменных окружения.');
            return;
        }

        // Получаем текущий домен приложения
        const currentDomain = window.location.origin;
        console.log('Текущий домен приложения:', currentDomain);

        // Создаем скрипт для Telegram Login Widget
        const script = document.createElement('script');
        script.src = 'https://telegram.org/js/telegram-widget.js?22';
        script.async = true;
        script.setAttribute('data-telegram-login', botName);
        script.setAttribute('data-size', 'large');
        script.setAttribute('data-radius', '8');
        script.setAttribute('data-request-access', 'read');
        script.setAttribute('data-userpic', 'true');
        script.setAttribute('data-auth-url', `${currentDomain}/auth/callback`);
        script.setAttribute('data-onauth', 'TelegramLoginWidget.dataOnauth(user)');

        console.log('Настройка Telegram Login Widget:', {
            botName,
            scriptSrc: script.src,
            domain: currentDomain,
            attributes: {
                'data-telegram-login': script.getAttribute('data-telegram-login'),
                'data-size': script.getAttribute('data-size'),
                'data-radius': script.getAttribute('data-radius'),
                'data-request-access': script.getAttribute('data-request-access'),
                'data-userpic': script.getAttribute('data-userpic'),
                'data-auth-url': script.getAttribute('data-auth-url'),
                'data-onauth': script.getAttribute('data-onauth')
            }
        });

        // Добавляем обработчик авторизации в глобальный объект
        window.TelegramLoginWidget = {
            dataOnauth: async (user: any) => {
                try {
                    setLoading(true);
                    setError(null);
                    console.log('Получены данные от Telegram:', user);

                    if (!user || !user.id) {
                        throw new Error('Получены некорректные данные от Telegram');
                    }

                    console.log('Вызываем handleTelegramAuth с данными:', JSON.stringify(user));
                    const userData = await handleTelegramAuth(user);
                    console.log('Результат handleTelegramAuth:', userData);

                    if (userData.success && userData.user) {
                        console.log('Авторизация успешна:', userData.user);
                        onAuth(userData);
                    } else {
                        console.error('Ошибка авторизации:', userData.error);
                        setError(userData.error || 'Ошибка при авторизации через Telegram');
                    }
                } catch (err) {
                    console.error('Ошибка при авторизации через Telegram:', err);
                    setError(err instanceof Error ? err.message : 'Ошибка при авторизации через Telegram');
                } finally {
                    setLoading(false);
                }
            }
        };

        // Очищаем контейнер и добавляем скрипт
        if (telegramRef.current) {
            telegramRef.current.innerHTML = '';
            telegramRef.current.appendChild(script);
            console.log('Скрипт Telegram Login Widget добавлен в DOM');
        }

        return () => {
            // Удаляем скрипт при размонтировании компонента
            if (telegramRef.current) {
                const scriptElement = telegramRef.current.querySelector('script');
                if (scriptElement) {
                    telegramRef.current.removeChild(scriptElement);
                }
            }
        };
    }, [botName, onAuth]);

    return (
        <div className="flex flex-col items-center">
            {error && (
                <div className="mb-4 p-4 bg-error text-error-content rounded-lg text-sm">
                    <p className="font-semibold mb-1">Ошибка авторизации:</p>
                    <p>{error}</p>
                    <p className="text-xs mt-2">Попробуйте обновить страницу или свяжитесь с техподдержкой.</p>
                </div>
            )}

            {loading ? (
                <div className="mb-4 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    <span className="ml-2">Проверка данных...</span>
                </div>
            ) : (
                <div ref={telegramRef} className="telegram-login"></div>
            )}

            <div className="mt-4 text-sm text-center text-neutral-content px-4">
                <p className="opacity-70">
                    Нажмите на кнопку выше, чтобы авторизоваться через Telegram.
                    Это безопасный способ входа без пароля.
                </p>
            </div>
        </div>
    );
};

export default TelegramAuth; 